import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/** Disclosure popover (menu button pattern): Escape and outside click close it; focus returns to the trigger. */
export function Popover({
  trigger, children, align = 'left', label,
}: {
  trigger: (p: { open: boolean; toggle: () => void; props: Record<string, unknown> }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        (wrap.current?.querySelector('[aria-haspopup]') as HTMLElement | null)?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    setTimeout(() => (wrap.current?.querySelector('.ex-pop button, .ex-pop [tabindex]') as HTMLElement | null)?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const close = () => {
    setOpen(false);
    (wrap.current?.querySelector('[aria-haspopup]') as HTMLElement | null)?.focus();
  };
  return (
    <div className="ex-popwrap" ref={wrap}>
      {trigger({ open, toggle: () => setOpen((o) => !o), props: { 'aria-haspopup': 'dialog', 'aria-expanded': open, 'aria-controls': id } })}
      {open ? (
        <div className={'ex-pop' + (align === 'right' ? ' is-right' : '')} id={id} role="dialog" aria-label={label}>
          {children(close)}
        </div>
      ) : null}
    </div>
  );
}
